/**
 * @theme/events — typed cross-component event bus.
 *
 * Every theme-level event gets:
 *   1. A name in `ThemeEvents` (string constant).
 *   2. A typed Event subclass with a structured `detail`.
 *
 * Dispatch:
 *     document.dispatchEvent(new CartUpdateEvent({ itemCount, items, source: 'add' }));
 *
 * Listen (auto-cleanup via Component.subscribe):
 *     this.subscribe(ThemeEvents.cartUpdate, (event) => { ... });
 *
 * All events bubble and compose so they cross Shadow DOM boundaries.
 */

export const ThemeEvents = Object.freeze({
  // Cart
  cartUpdate: 'theme:cart:update',
  cartError: 'theme:cart:error',
  cartItemAdded: 'theme:cart:item-added',
  cartItemRemoved: 'theme:cart:item-removed',
  cartDiscountApplied: 'theme:cart:discount-applied',
  cartDiscountRemoved: 'theme:cart:discount-removed',

  // Quantity
  quantityUpdate: 'theme:quantity:update',

  // Variant / product
  variantSelected: 'theme:variant:selected',
  variantUpdate: 'theme:variant:update',
  productMediaChanged: 'theme:product:media-changed',

  // Filter / search
  filterUpdate: 'theme:filter:update',
  searchOpen: 'theme:search:open',
  searchClose: 'theme:search:close',
  searchSubmit: 'theme:search:submit',

  // Drawer / modal
  drawerOpen: 'theme:drawer:open',
  drawerClose: 'theme:drawer:close',
  modalOpen: 'theme:modal:open',
  modalClose: 'theme:modal:close',

  // Media
  mediaStartedPlaying: 'theme:media:playing',
  mediaPaused: 'theme:media:paused',
  zoomMediaSelected: 'theme:zoom:selected',

  // UI
  quickAddOpen: 'theme:quick-add:open',
  quickAddClose: 'theme:quick-add:close',
  recentlyViewedUpdate: 'theme:recently-viewed:update',
  toastShow: 'theme:toast:show',
  toastDismiss: 'theme:toast:dismiss',

  // Section rendering
  sectionRendered: 'theme:section:rendered',

  // Navigation
  pageNavigate: 'theme:page:navigate',
  viewTransitionStart: 'theme:view-transition:start',
  viewTransitionEnd: 'theme:view-transition:end',

  // Localization
  countryUpdate: 'theme:country:update',
  languageUpdate: 'theme:language:update',
});

const baseInit = { bubbles: true, composed: true };

class TypedEvent extends Event {
  /**
   * @param {string} type
   * @param {object} detail
   */
  constructor(type, detail) {
    super(type, baseInit);
    /** @type {any} */
    this.detail = detail;
  }
}

/* -------------------------------------------------------------------------- */
/* Cart events                                                                */
/* -------------------------------------------------------------------------- */

export class CartUpdateEvent extends TypedEvent {
  /**
   * @param {{ itemCount: number, items?: any[], source?: 'add'|'remove'|'change'|'replace'|'fetch'|'optimistic', sectionsToRender?: string[] }} detail
   */
  constructor(detail) { super(ThemeEvents.cartUpdate, detail); }
}

export class CartErrorEvent extends TypedEvent {
  /** @param {{ message: string, status?: number, source?: string, error?: unknown }} detail */
  constructor(detail) { super(ThemeEvents.cartError, detail); }
}

export class CartItemAddedEvent extends TypedEvent {
  /** @param {{ variantId: number|string, quantity: number, item?: any }} detail */
  constructor(detail) { super(ThemeEvents.cartItemAdded, detail); }
}

export class CartItemRemovedEvent extends TypedEvent {
  /** @param {{ key: string, variantId?: number|string }} detail */
  constructor(detail) { super(ThemeEvents.cartItemRemoved, detail); }
}

/* -------------------------------------------------------------------------- */
/* Quantity                                                                   */
/* -------------------------------------------------------------------------- */

export class QuantityUpdateEvent extends TypedEvent {
  /** @param {{ key: string, quantity: number, optimistic?: boolean }} detail */
  constructor(detail) { super(ThemeEvents.quantityUpdate, detail); }
}

/* -------------------------------------------------------------------------- */
/* Variant / product                                                          */
/* -------------------------------------------------------------------------- */

export class VariantSelectedEvent extends TypedEvent {
  /** @param {{ variant: any, productId: number|string, sectionId: string }} detail */
  constructor(detail) { super(ThemeEvents.variantSelected, detail); }
}

export class VariantUpdateEvent extends TypedEvent {
  /** @param {{ variant: any, productId: number|string, sectionId: string, html?: string }} detail */
  constructor(detail) { super(ThemeEvents.variantUpdate, detail); }
}

export class ProductMediaChangedEvent extends TypedEvent {
  /** @param {{ mediaId: number|string, sectionId: string }} detail */
  constructor(detail) { super(ThemeEvents.productMediaChanged, detail); }
}

/* -------------------------------------------------------------------------- */
/* Filter / search                                                            */
/* -------------------------------------------------------------------------- */

export class FilterUpdateEvent extends TypedEvent {
  /** @param {{ url: string, sectionsToRender?: string[] }} detail */
  constructor(detail) { super(ThemeEvents.filterUpdate, detail); }
}

export class SearchOpenEvent extends TypedEvent {
  /** @param {{ source?: 'icon'|'shortcut'|'mobile' }} detail */
  constructor(detail = {}) { super(ThemeEvents.searchOpen, detail); }
}

export class SearchCloseEvent extends TypedEvent {
  constructor(detail = {}) { super(ThemeEvents.searchClose, detail); }
}

export class SearchSubmitEvent extends TypedEvent {
  /** @param {{ query: string }} detail */
  constructor(detail) { super(ThemeEvents.searchSubmit, detail); }
}

/* -------------------------------------------------------------------------- */
/* Drawer / modal                                                             */
/* -------------------------------------------------------------------------- */

export class DrawerOpenEvent extends TypedEvent {
  /** @param {{ id: string, source?: string }} detail */
  constructor(detail) { super(ThemeEvents.drawerOpen, detail); }
}

export class DrawerCloseEvent extends TypedEvent {
  /** @param {{ id: string }} detail */
  constructor(detail) { super(ThemeEvents.drawerClose, detail); }
}

export class ModalOpenEvent extends TypedEvent {
  /** @param {{ id: string, source?: string }} detail */
  constructor(detail) { super(ThemeEvents.modalOpen, detail); }
}

export class ModalCloseEvent extends TypedEvent {
  /** @param {{ id: string }} detail */
  constructor(detail) { super(ThemeEvents.modalClose, detail); }
}

/* -------------------------------------------------------------------------- */
/* Media                                                                      */
/* -------------------------------------------------------------------------- */

export class MediaPlayingEvent extends TypedEvent {
  /** @param {{ id: string, mediaType: 'video'|'model'|'audio'|'image' }} detail */
  constructor(detail) { super(ThemeEvents.mediaStartedPlaying, detail); }
}

export class MediaPausedEvent extends TypedEvent {
  /** @param {{ id: string }} detail */
  constructor(detail) { super(ThemeEvents.mediaPaused, detail); }
}

export class ZoomMediaSelectedEvent extends TypedEvent {
  /** @param {{ mediaId: string|number, sectionId: string }} detail */
  constructor(detail) { super(ThemeEvents.zoomMediaSelected, detail); }
}

/* -------------------------------------------------------------------------- */
/* Quick-add / recently viewed / toasts                                       */
/* -------------------------------------------------------------------------- */

export class QuickAddOpenEvent extends TypedEvent {
  /** @param {{ productHandle: string, source?: string }} detail */
  constructor(detail) { super(ThemeEvents.quickAddOpen, detail); }
}

export class QuickAddCloseEvent extends TypedEvent {
  constructor(detail = {}) { super(ThemeEvents.quickAddClose, detail); }
}

export class RecentlyViewedUpdateEvent extends TypedEvent {
  /** @param {{ products: Array<{ id: number|string, handle: string, addedAt: number }> }} detail */
  constructor(detail) { super(ThemeEvents.recentlyViewedUpdate, detail); }
}

export class ToastShowEvent extends TypedEvent {
  /** @param {{ message: string, kind?: 'info'|'success'|'warning'|'error', duration?: number, action?: { label: string, href?: string, onClick?: () => void } }} detail */
  constructor(detail) { super(ThemeEvents.toastShow, detail); }
}

export class ToastDismissEvent extends TypedEvent {
  /** @param {{ id?: string }} detail */
  constructor(detail = {}) { super(ThemeEvents.toastDismiss, detail); }
}

/* -------------------------------------------------------------------------- */
/* Section rendering                                                          */
/* -------------------------------------------------------------------------- */

export class SectionRenderedEvent extends TypedEvent {
  /** @param {{ sectionId: string, source: 'cart'|'facets'|'variant'|'manual' }} detail */
  constructor(detail) { super(ThemeEvents.sectionRendered, detail); }
}

/* -------------------------------------------------------------------------- */
/* Navigation / view transitions                                              */
/* -------------------------------------------------------------------------- */

export class PageNavigateEvent extends TypedEvent {
  /** @param {{ url: string, source?: 'click'|'history'|'speculation' }} detail */
  constructor(detail) { super(ThemeEvents.pageNavigate, detail); }
}

export class ViewTransitionStartEvent extends TypedEvent {
  /** @param {{ name?: string }} detail */
  constructor(detail = {}) { super(ThemeEvents.viewTransitionStart, detail); }
}

export class ViewTransitionEndEvent extends TypedEvent {
  /** @param {{ name?: string }} detail */
  constructor(detail = {}) { super(ThemeEvents.viewTransitionEnd, detail); }
}

/* -------------------------------------------------------------------------- */
/* Localization                                                               */
/* -------------------------------------------------------------------------- */

export class CountryUpdateEvent extends TypedEvent {
  /** @param {{ countryCode: string, currency?: string }} detail */
  constructor(detail) { super(ThemeEvents.countryUpdate, detail); }
}

export class LanguageUpdateEvent extends TypedEvent {
  /** @param {{ languageCode: string }} detail */
  constructor(detail) { super(ThemeEvents.languageUpdate, detail); }
}

export default ThemeEvents;
